import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import DashboardEmptyState from "../components/DashboardEmptyState";
import { dashboardTheme } from "../theme/dashboardTheme";
import { IPropertyAnalyticsItem } from "../types/ownerDashboard.types";
import {
  resolvePropertyAnalyticsDisplayName,
  resolvePropertyAnalyticsLabel,
} from "../utils/buildPropertyAnalyticsFilterOptions";
import { PROPERTY_ANALYTICS_TOP_PROPERTIES_LIMIT } from "./constants";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

interface PropertyAnalyticsBarChartProps {
  items: IPropertyAnalyticsItem[];
}

function truncateLabel(label: string, maxLength = 36): string {
  if (label.length <= maxLength) {
    return label;
  }

  return `${label.slice(0, maxLength - 1)}…`;
}

export default function PropertyAnalyticsBarChart({ items }: PropertyAnalyticsBarChartProps) {
  const topItems = useMemo(
    () => items.slice(0, PROPERTY_ANALYTICS_TOP_PROPERTIES_LIMIT),
    [items]
  );

  const fullLabels = useMemo(
    () => topItems.map((item) => resolvePropertyAnalyticsDisplayName(item)),
    [topItems]
  );

  const chartData = useMemo(
    () => ({
      labels: topItems.map((item) =>
        truncateLabel(resolvePropertyAnalyticsLabel(item))
      ),
      datasets: [
        {
          data: topItems.map((item) => item.screen_page_views),
          backgroundColor: "rgba(110, 168, 255, 0.75)",
          borderRadius: 4,
          maxBarThickness: 18,
        },
      ],
    }),
    [topItems]
  );

  const options = useMemo(
    () => ({
      indexAxis: "y" as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: dashboardTheme.colors.surfaceElevated,
          borderColor: dashboardTheme.colors.cardBorder,
          borderWidth: 1,
          titleColor: dashboardTheme.colors.textPrimary,
          bodyColor: dashboardTheme.colors.textSecondary,
          callbacks: {
            title: (tooltipItems: { dataIndex: number }[]) => {
              const index = tooltipItems[0]?.dataIndex;
              return index != null ? fullLabels[index] : "";
            },
            label: (context: { parsed: { x: number | null } }) =>
              `Visualizações: ${context.parsed.x ?? 0}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: dashboardTheme.colors.chartGrid },
          ticks: {
            color: dashboardTheme.colors.chartTick,
            precision: 0,
          },
        },
        y: {
          grid: { display: false },
          ticks: {
            color: dashboardTheme.colors.chartTick,
            font: { size: 10 },
          },
        },
      },
    }),
    [fullLabels]
  );

  if (topItems.length === 0) {
    return (
      <DashboardEmptyState
        align="center"
        title="Sem ranking de imóveis"
        description="Não há dados de visualização por imóvel neste período."
        className="flex h-48 items-center justify-center"
      />
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <p className="text-xs font-semibold text-[#8b949e]">
        Top {PROPERTY_ANALYTICS_TOP_PROPERTIES_LIMIT} imóveis por visualizações
      </p>
      <div className="h-56 w-full min-w-0 sm:h-72">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
}
